/**
 * Simpli.fi Pixel Block
 *
 * Injection block for a property's two Simpli.fi sifitags. The universal tag
 * loads once for site-wide retargeting. The lead tag records a conversion
 * each time it loads, so it is re-requested on every lead event from the bus.
 */

import { useEffect } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken, useVibesiteEvents } from '@vibesites/runtime/react';
import { Event, type VibesiteEventName } from '@vibesites/runtime';
import { useTrackingAllowed } from '../../shared/analytics';

const UNIVERSAL_SCRIPT_ID = 'simplifi-universal-pixel-script';
const LEAD_SCRIPT_ID = 'simplifi-lead-pixel-script';
const LEAD_EVENTS: VibesiteEventName[] = [Event.contact_form_submitted, Event.tour_scheduled];
const GUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// Accepts the bare GUID, the sifitag URL, or the full script tag.
function sifitagUrl(value: string | null): string | null {
  const guid = value?.match(GUID)?.[0];
  return guid ? `https://tag.simpli.fi/sifitag/${guid}` : null;
}

function appendScript(id: string, src: string) {
  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  document.head.appendChild(script);
}

function SimplifiPixelComponent(_props: BlockProps) {
  const universalUrl = sifitagUrl(
    useBlockToken('universal_pixel_id', { defaultField: 'custom_simplifi_universal_pixel' }),
  );
  const leadUrl = sifitagUrl(
    useBlockToken('lead_pixel_id', { defaultField: 'custom_simplifi_lead_pixel' }),
  );
  const { subscribe } = useVibesiteEvents();
  const trackingAllowed = useTrackingAllowed('advertising');

  useEffect(() => {
    if (!trackingAllowed || !universalUrl) return;
    if (document.getElementById(UNIVERSAL_SCRIPT_ID)) return;
    appendScript(UNIVERSAL_SCRIPT_ID, universalUrl);
  }, [trackingAllowed, universalUrl]);

  useEffect(() => {
    if (!trackingAllowed || !leadUrl) return;

    return subscribe('*', (name) => {
      if (!LEAD_EVENTS.includes(name)) return;
      // A script element runs once; only a new element re-requests the tag.
      document.getElementById(LEAD_SCRIPT_ID)?.remove();
      appendScript(LEAD_SCRIPT_ID, leadUrl);
    });
  }, [trackingAllowed, leadUrl, subscribe]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'SimplifiPixel',
  displayName: 'Simpli.fi Pixel',
  tokens: {
    universal_pixel_id: {
      label: 'Universal Pixel ID',
      type: 'text',
      defaultField: 'custom_simplifi_universal_pixel',
      suggestedFields: ['custom_simplifi_universal_pixel'],
      description:
        'GUID at the end of the universal sifitag URL (https://tag.simpli.fi/sifitag/<GUID>). Loads on every page. The full URL or script tag also works.',
    },
    lead_pixel_id: {
      label: 'Lead Pixel ID',
      type: 'text',
      defaultField: 'custom_simplifi_lead_pixel',
      suggestedFields: ['custom_simplifi_lead_pixel'],
      description:
        'GUID at the end of the lead sifitag URL (https://tag.simpli.fi/sifitag/<GUID>). Loads once per submitted lead form or scheduled tour. The full URL or script tag also works.',
    },
  },
};

export const simplifiPixelBlock: BlockDefinition = {
  id: 'simplifi-pixel',
  name: 'Simpli.fi Pixel',
  description:
    'Simpli.fi universal retargeting tag on every page, plus the lead conversion tag on each lead form submission or scheduled tour.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'template',
  component: SimplifiPixelComponent,
  contentSchema,
  tags: ['simplifi', 'simpli.fi', 'sifitag', 'pixel', 'tracking', 'ads', 'conversion'],
  trackedEvents: LEAD_EVENTS.map((event) => ({
    event,
    mapsTo: 'Simpli.fi lead sifitag load',
    description: 'Fires only when the Lead Pixel ID is set.',
  })),
};
